import { useEffect, useState } from "react";
import {
  deleteIcon,
  editIcon,
  filterIcon,
  timeLineIcon,
} from "../../assets/icons/SvgIconList";
import DeleteBlockedModal from "../../components/purchase/DeleteBlockedModal";
import ActionIcon from "../../components/purchase/ActionIcon";
import StatusChip from "../../components/purchase/StatusChip";
import { ICDropDown } from "../../core-component/form";
import {
  AdvanceSearch,
  ICButton,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import { IPagination } from "../../data/AppInterface";
import { TBitValue } from "../../data/AppType";
import UseEventCallbackHook from "../../hooks/UseEventCallbackHook";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { ICreditPlanFilter } from "../../services/credit-plan/CreditPlanService";
import { ORDER_BY } from "../../utils/AppConstants";
import { AUDIT_LOG_TYPE, BIT_VALUE } from "../../utils/AppEnumerations";
import {
  escapeHtml,
  prepareMessageFromParams,
  toggleStatus,
} from "../../utils/AppFunctions";
import {
  CREDIT_TYPE_CHIP_CLASS,
  CREDIT_TYPE_LABEL_KEY,
  getActiveStatusOptions,
  getCreditTypeOptions,
  getVisibilityTypeOptions,
  VISIBILITY_TYPE_CHIP_CLASS,
  VISIBILITY_TYPE_LABEL_KEY,
} from "../../utils/PurchaseConstants";
import AuditLog from "../audit-log/AuditLogView";
import CreditPlanForm from "./CreditPlanForm";
import {
  activeInactiveCreditPlan,
  CREDIT_PLAN_FILTER_INITIAL_VALUE,
  deleteCreditPlan,
  getAllCreditPlan,
  getCreditPlanFields,
  ICreditPlan,
} from "./CreditPlanViewController";

let timer: ReturnType<typeof setTimeout>;

const CreditPlanView = () => {
  const { t } = UseTranslationHook();
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ICreditPlanFilter>(CREDIT_PLAN_FILTER_INITIAL_VALUE);
  const { pagination, setChangePagination } = UsePaginationHook();
  const [creditPlanList, setCreditPlanList] = useState<ICreditPlan[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<ICreditPlan>();
  const [planToDelete, setPlanToDelete] = useState<ICreditPlan>();
  const [deleteBlock, setDeleteBlock] = useState<{ plan: ICreditPlan; message: string }>();
  const [planForHistory, setPlanForHistory] = useState<ICreditPlan>();

  const fetchCreditPlan = (paginationPayload: IPagination) => {
    setIsLoading(true);
    if (timer) clearTimeout(timer);
    timer = setTimeout(async () => {
      await getAllCreditPlan(setCreditPlanList, paginationPayload, setChangePagination, search, filter);
      setIsLoading(false);
    }, 500);
  };

  const fetchFirstPage = UseEventCallbackHook(() =>
    fetchCreditPlan({ ...pagination, currentPage: 1 }),
  );

  useEffect(() => {
    fetchFirstPage();
  }, [search, filter, fetchFirstPage]);

  const setPlanStatus = async (plan: ICreditPlan, value: TBitValue) => {
    const isUpdated = await activeInactiveCreditPlan(plan.id, value);
    if (isUpdated) setCreditPlanList(toggleStatus(creditPlanList, plan.id));
    return isUpdated;
  };

  const handleStatus = (plan: ICreditPlan) =>
    setPlanStatus(plan, plan.is_active === BIT_VALUE.One ? BIT_VALUE.Zero : BIT_VALUE.One);

  const handleDelete = async (confirmed: boolean) => {
    const plan = planToDelete;
    setPlanToDelete(undefined);
    if (!confirmed || !plan) return;
    const blockMessage = await deleteCreditPlan(plan.id);
    if (blockMessage) {
      setDeleteBlock({ plan, message: blockMessage });
      return;
    }
    fetchCreditPlan(pagination);
  };

  const handleCloseForm = (status: boolean) => {
    setShowForm(false);
    setSelectedPlan(undefined);
    if (status) fetchCreditPlan(pagination);
  };

  const creditTypeCell = (plan: ICreditPlan) => (
    <StatusChip
      label={t(CREDIT_TYPE_LABEL_KEY[plan.credit_type])}
      className={CREDIT_TYPE_CHIP_CLASS[plan.credit_type]}
    />
  );

  const typeCell = (plan: ICreditPlan) => (
    <StatusChip
      label={t(VISIBILITY_TYPE_LABEL_KEY[plan.visibility_type])}
      className={VISIBILITY_TYPE_CHIP_CLASS[plan.visibility_type]}
    />
  );

  const customActionView = (plan: ICreditPlan) => (
    <div className="flex items-center justify-center w-full gap-2">
      <ActionIcon
        icon={editIcon}
        title={t("edit_tooltip_icon")}
        onClick={() => {
            setSelectedPlan(plan);
            setShowForm(true);
          }}
      />
      <ActionIcon
        icon={deleteIcon}
        title={t("delete_tooltip_icon")}
        onClick={() => setPlanToDelete(plan)}
      />
      <ActionIcon
        icon={timeLineIcon}
        title={t("change_history")}
        onClick={() => setPlanForHistory(plan)}
      />
    </div>
  );

  const filterView = (
    <div className="w-full grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
      <ICDropDown
        placeholder={t("credit_type")}
        selected={filter.credit_type ?? undefined}
        options={getCreditTypeOptions(t)}
        onSelect={(option) => setFilter((previous) => ({ ...previous, credit_type: option.data_value }))}
      />
      <ICDropDown
        placeholder={t("type")}
        selected={filter.visibility_type ?? undefined}
        options={getVisibilityTypeOptions(t, false)}
        onSelect={(option) =>
          setFilter((previous) => ({ ...previous, visibility_type: option.data_value }))
        }
      />
      <ICDropDown
        placeholder={t("status")}
        selected={filter.is_active ?? undefined}
        options={getActiveStatusOptions(t)}
        onSelect={(option) => setFilter((previous) => ({ ...previous, is_active: option.data_value }))}
      />
      <ICButton
        type="button"
        onClick={() => setFilter(CREDIT_PLAN_FILTER_INITIAL_VALUE)}
        className="flex !w-fit"
      >
        <div className="size-5 grid place-self-center">{filterIcon}</div>
        &nbsp;{t("clear")}
      </ICButton>
    </div>
  );

  return (
    <div className="w-full overflow-scroll scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() => setShowForm(true)}
        handleSearchChange={setSearch}
        searchText={search}
        filterNode={filterView}
        refreshTable={() => fetchCreditPlan(pagination)}
        title={t("credit_plans")}
      />
      <ICTable<ICreditPlan>
        isLoading={isLoading}
        totalFlex="grid grid-cols-16"
        fields={getCreditPlanFields(
          { action: customActionView, creditType: creditTypeCell, type: typeCell },
          handleStatus,
          t,
        )}
        data={creditPlanList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRows) =>
          fetchCreditPlan({ ...pagination, perPageRows, currentPage: 1 })
        }
        handleChangePage={(page) => fetchCreditPlan({ ...pagination, currentPage: page })}
        handleSort={(field) =>
          fetchCreditPlan({
            ...pagination,
            sortBy: field.key,
            orderBy: pagination.orderBy === ORDER_BY.desc ? ORDER_BY.asc : ORDER_BY.desc,
          })
        }
      />
      {showForm && (
        <CreditPlanForm handleClose={handleCloseForm} creditPlanToEdit={selectedPlan} />
      )}
      {planToDelete && (
        <ICConfirmationModal
          title={t("delete_credit_plan")}
          message={prepareMessageFromParams(t("delete_message"), [
            ["fieldName", escapeHtml(planToDelete.name)],
          ])}
          onClose={handleDelete}
        />
      )}
      {deleteBlock && (
        <DeleteBlockedModal
          title={t("cant_delete_credit_plan")}
          message={deleteBlock.message}
          onClose={() => setDeleteBlock(undefined)}
          onMakeInactive={
            deleteBlock.plan.is_active === BIT_VALUE.One
              ? async () => {
                  await setPlanStatus(deleteBlock.plan, BIT_VALUE.Zero);
                  setDeleteBlock(undefined);
                }
              : undefined
          }
        />
      )}
      {planForHistory && (
        <AuditLog
          handleClose={() => setPlanForHistory(undefined)}
          auditLogRecordId={planForHistory.id}
          auditLogType={AUDIT_LOG_TYPE.CreditPlan}
          titleName={planForHistory.name}
        />
      )}
    </div>
  );
};

export default CreditPlanView;
